import {
    FunctionComponent,
    ChangeEvent,
    useMemo,
    useEffect,
    useState,
} from "react"
import { Table } from "@tanstack/react-table"
import { UsersAdminTableDataI } from "../types"
import styles from "./actionHeader.module.scss"
import Input from "../../shared/input"
import { ORGANIZATION_COLUMN_ID, STATUS_COLUMN_ID } from "../column"
import { Toggle } from "../../shared/selectionControls"
import { useBreakPointContext } from "../../../context/breakPointContext"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    faChevronDoubleDown,
    faChevronDoubleUp,
} from "@fortawesome/pro-solid-svg-icons"
import Button from "../../shared/button"
import classNames from "classnames"
import Policy_OrganizationMembership from "../../../rolePolicy/organizationMembership"
import { useViewerContext } from "../../../context/ViewerContext"

interface UsersAdminActionHeaderI {
    table: Table<UsersAdminTableDataI>
    globalFilter: string
}

const UsersAdminActionHeader: FunctionComponent<UsersAdminActionHeaderI> = ({
    table,
    globalFilter,
}) => {
    const { getViewer } = useViewerContext()
    const { role: viewerRole } = getViewer()

    const breakPoint = useBreakPointContext()
    const is_LE_mediumBreakPoint = breakPoint === "sm" || breakPoint === "md"
    const [showActions, setShowActions] = useState(true)
    const organizationColumn = table.getColumn(ORGANIZATION_COLUMN_ID)
    const activeColumn = table.getColumn(STATUS_COLUMN_ID)

    const organizationOptions = useMemo(() => {
        const o = organizationColumn?.getFacetedUniqueValues().keys()
        return o ? Array.from(o).sort() : []
    }, [organizationColumn])
    const organizationFilterValue = organizationColumn?.getFilterValue() || ""
    const showOrganizationFilter = organizationOptions.length > 1

    // if we resize from mobile => desktop sizing we need to setShowActions to true
    useEffect(() => {
        if (!is_LE_mediumBreakPoint) {
            setShowActions(true)
        }
    }, [is_LE_mediumBreakPoint])

    // font awesome icon
    const fontAwesomeIcon = showActions
        ? faChevronDoubleUp
        : faChevronDoubleDown

    // styling classes in mobile
    const outerClass =
        is_LE_mediumBreakPoint && !showActions
            ? styles.outerClosed
            : styles.outer

    const showActiveToggle =
        !Policy_OrganizationMembership.canOnlyFetchActiveUsers(viewerRole)

    return (
        <div className={outerClass}>
            {is_LE_mediumBreakPoint && (
                <Button
                    handleButtonClick={() => setShowActions(!showActions)}
                    status="primary"
                    customButtonClassName={styles.mobileButton}
                >
                    <FontAwesomeIcon icon={fontAwesomeIcon} color="#fff" />
                </Button>
            )}
            {showActions && (
                <div className={styles.container}>
                    <div className={styles.containerLeft}>
                        {showActiveToggle && (
                            <div className={styles.filter}>
                                <div className={styles.toggleContainer}>
                                    <span
                                        className={classNames(styles.label, {
                                            [styles.labelActive]:
                                                activeColumn?.getFilterValue() as boolean,
                                            [styles.labelInactive]:
                                                !(activeColumn?.getFilterValue() as boolean),
                                        })}
                                    >
                                        Active Only:
                                    </span>
                                    <div>
                                        <Toggle
                                            checked={
                                                activeColumn?.getFilterValue() as boolean
                                            }
                                            handleToggleClick={() => {
                                                activeColumn?.setFilterValue(
                                                    !(activeColumn?.getFilterValue() as boolean)
                                                )
                                            }}
                                            toggleLabel=""
                                            disabled={Policy_OrganizationMembership.canOnlyFetchActiveUsers(
                                                viewerRole
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className={styles.filter}>
                            {showOrganizationFilter && (
                                <Input
                                    onChange={(
                                        e: ChangeEvent<HTMLInputElement>
                                    ) => {
                                        organizationColumn?.setFilterValue(
                                            e.target.value
                                        )
                                    }}
                                    type="datalist"
                                    orientation="horizontal"
                                    placeHolder="Filter Organizations"
                                    datalistId="organizations"
                                    options={organizationOptions}
                                    specializedClass="noMessage"
                                    inputCustomClass={styles.organizationFilter}
                                    value={organizationFilterValue as string}
                                />
                            )}
                        </div>
                    </div>
                    <div className={styles.containerRight}>
                        <Input
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                table.setGlobalFilter(e.target.value)
                            }
                            type="text"
                            orientation="horizontal"
                            placeHolder="Search Columns"
                            specializedClass="noMessage"
                            value={globalFilter ?? ""}
                        />
                        <Button
                            handleButtonClick={() => {
                                table.setGlobalFilter("")
                            }}
                            status="secondary"
                            condition="default"
                        >
                            Clear
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default UsersAdminActionHeader
